import { ConfirmDialog } from '@/components/feature/confirm-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FileData } from '@/types/file';
import { Link2, MoreHorizontal, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { copyToClipboard } from '@/lib/utils/clipboard';

interface FileDataTableRowActionsProps {
  file: FileData;
  onDelete: (file: FileData) => Promise<void>;
}

export const FileDataTableRowActions = ({ file, onDelete }: FileDataTableRowActionsProps) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleCopyLink = async (e: React.MouseEvent | React.TouchEvent) => {
    // Prevent any default behavior
    e.preventDefault();
    e.stopPropagation();
    
    try {
      // Close dropdown before attempting copy (helps with iOS)
      setIsOpen(false);
      
      // Small delay to ensure dropdown is closed
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const success = await copyToClipboard(file.url);
      if (success) {
        toast.success('Link copied to clipboard!');
      } else {
        throw new Error('Copy operation failed');
      }
    } catch (error) {
      console.error('Error copying URL:', error);
      toast.error('Failed to copy link');
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await onDelete(file);
      setShowDeleteConfirm(false);
      setIsOpen(false);
    } catch {
      toast.error('Failed to delete file');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuItem 
            onTouchEnd={handleCopyLink}
            onClick={handleCopyLink}
            className="cursor-pointer"
          >
            <Link2 className="mr-2 h-4 w-4" />
            Copy Link
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onTouchEnd={(e) => {
              e.preventDefault();
              setShowDeleteConfirm(true);
            }}
            onClick={() => setShowDeleteConfirm(true)}
            className="text-destructive focus:text-destructive cursor-pointer"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete File"
        description={`Are you sure you want to delete "${file.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="destructive"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </>
  );
};
